<template>
  <div>
    <div v-if="!isChatsRoute" id="embedded_chats_panel" class="fixed bottom-0 right-6 z-[100] flex flex-row items-end gap-2 pointer-events-none">
      <!-- Open Chat Windows (left of sidebar) -->
      <EmbeddedChatWindow
        v-for="entry in openChats"
        :key="entry.chat?.id"
        :chat-entry="entry"
        :max-body-height="maxChatWindowHeight - 45"
      />

      <!-- Sidebar Panel -->
      <div class="flex flex-col items-end w-[300px] pointer-events-auto">
        <!-- Header (always visible) -->
        <div
          class="embedded_chats_surface flex items-center gap-2 px-4 py-2 rounded-t-lg cursor-pointer select-none w-full"
          @click="isExpanded = !isExpanded"
        >
          <span class="flex-1 text-sm font-semibold text_color truncate">Chat</span>
          <div
            v-if="!isExpanded && totalUnreadCount > 0"
            class="flex items-center justify-center text-xs text-white font-semibold pt-[1px] px-[4px] leading-[14px] !min-w-[16px] count_tag"
          >
            {{ totalUnreadCount }}
          </div>
          <ThemeImage
              :icon="imageNames.LINK_GREY"
              class="opacity-80 cursor-pointer size-4"
              alt="chat"
              @click.stop="openMainChats"
          />
          <ThemeImage
            :icon="isExpanded ? imageNames.BOTTOM_ARROW : imageNames.UP_ARROW"
            class="opacity-40"
            :class="isExpanded ? 'size-5' : 'size-3.5'"
            :alt="isExpanded ? 'collapse' : 'expand'"
          />
        </div>

        <!-- Expandable Content (animated) -->
        <div
          class="embedded_chats_surface embedded_panel_body w-full"
          :class="isExpanded ? 'embedded_panel_body--open' : 'embedded_panel_body--closed'"
          :style="isExpanded ? { height: (maxPanelHeight - 45) + 'px' } : {}"
        >
          <ChatsSidePanel />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, provide, watch, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ChatsSidePanel from "@/src/views/projects/chats/side_panel/ChatsSidePanel.vue";
import EmbeddedChatWindow from "@/src/components/projects_components/chats/EmbeddedChatWindow.vue";

const store = useStore();
const route = useRoute();
const router = useRouter();

const isChatsRoute = computed(() => route.path.includes("/projects/chats"));

const isExpanded = ref(localStorage.getItem("embedded_chats_sidebar_expanded") === "true");
const windowHeight = ref(window.innerHeight);

const maxPanelHeight = computed(() => Math.floor((windowHeight.value * 6) / 7));
const maxChatWindowHeight = computed(() => Math.floor((windowHeight.value * 3.2) / 5));
const openChats = computed(() => store.getters["embeddedChats/openChats"]);
const totalUnreadCount = computed(() => store.getters["chats/totalUnreadCount"] || 0);

// Provide embedded mode context for ChatList
const openEmbeddedChat = (chat) => {
  store.dispatch("embeddedChats/openChat", chat);
};

const openMainChats = async () => {
  await router.push("/projects/chats");
}

provide("embeddedChatMode", true);
provide("openEmbeddedChat", openEmbeddedChat);

function onResize() {
  windowHeight.value = window.innerHeight;
}

const groups = computed(() => store.getters["chats/groups"] || []);
const hasRestoredChats = ref(false);

watch(isExpanded, (val) => {
  localStorage.setItem("embedded_chats_sidebar_expanded", val ? "true" : "false");
});

watch(groups, (newGroups) => {
  if (!hasRestoredChats.value && newGroups.length > 0) {
    hasRestoredChats.value = true;
    store.dispatch("embeddedChats/restoreChats");
  }
}, { immediate: true });

onMounted(() => {
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.embedded_chats_surface {
  background-color: var(--background-color);
  box-shadow: var(--dropdown-boxshadow);
  border: 1px solid var(--white-opacity-8);
}

.embedded_panel_body {
  transition: height 0.3s ease, opacity 0.25s ease;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  overflow: hidden;
}

.embedded_panel_body--open {
  opacity: 1;
}

.embedded_panel_body--closed {
  height: 0 !important;
  opacity: 0;
  border: none;
  box-shadow: none;
}
</style>
